import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { authRepository } from "infra/repositories/auth-repository";
import { Logger } from "infra/logger/logger";
import {
  getSessionStorageItem,
  removeSessionStorageItem,
} from "presentation/utils/sessionStorage";
import { ProgressSpinner } from "primereact/progressspinner";
import moment from "moment";

import { useIsMobile } from "presentation/mobile/hooks/useIsMobile";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const isMobile = useIsMobile();

  const email = useAuthStore((state) => state.email);
  const accessToken = useAuthStore((state) => state.accessToken);
  const logger = Logger.getInstance();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;

    const handleAuthCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const authCode = params.get("code");

      if (authCode) {
        hasProcessed.current = true;
        try {
          const codeVerifier = getSessionStorageItem("codeVerifier");

          if (!codeVerifier) {
            throw new Error("Code verifier não encontrado.");
          }

          if (!accessToken) {
            const token = await authRepository.token(authCode, codeVerifier);
            if (token && token.data) {
              setAuth(token.data);
            }
          }
          logger.info(
            `Login by ${email} at ${moment().format("YYYY-MM-DD HH:mm:ss")}`,
          );
          logger.debug(
            `Login by ${email} at ${moment().format("YYYY-MM-DD HH:mm:ss")}`,
          );

          const targetRoute = isMobile ? "/mobile/dashboards" : "/dashboards";
          navigate(targetRoute, { replace: true });
        } catch (error: any) {
          console.error("Erro ao trocar o código por token:", error);
          logger.error("Login failed:" + error);

          // Mostrar mensagem mais específica baseada no tipo de erro
          let errorMessage = "Erro durante a autenticação. ";
          if (error.message?.includes("Timeout")) {
            errorMessage += "O servidor não está respondendo. Tente novamente.";
          } else if (error.message.includes("temporariamente indisponível")) {
            errorMessage +=
              "Serviço temporariamente indisponível. Tente novamente em alguns minutos.";
          } else if (error.message.includes("Erro interno do servidor")) {
            errorMessage +=
              "Erro interno do servidor. Contate o administrador do sistema.";
          } else if (error.message.includes("Token do Azure inválido")) {
            errorMessage +=
              "Problema na autenticação com Microsoft. Tente fazer login novamente.";
          } else if (error.message.includes("Erro ao obter token do Azure")) {
            errorMessage +=
              "Problema na comunicação com Microsoft. Verifique sua conexão.";
          } else {
            errorMessage += "Verifique sua conexão e tente novamente.";
          }

          alert(errorMessage);
          removeSessionStorageItem("codeVerifier");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };
    handleAuthCode();
  }, [accessToken, email, logger, navigate, setAuth]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#f8f9fa",
      }}
    >
      <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      <span style={{ marginTop: "1rem", color: "#495057", fontWeight: 500 }}>
        A validar credenciais...
      </span>
    </div>
  );
};

export default AuthCallback;
